import { Component, OnDestroy, OnInit } from '@angular/core'
import { Subject, Subscription } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { FormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator'
import { MatTabsModule } from '@angular/material/tabs'
import { MatIconModule } from '@angular/material/icon'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { NgClass } from '@angular/common'

import { AuthService } from '../../core/services/auth/auth.service'
import { MonitoringService } from '../../core/services/monitoring/monitoring.service'
import { MonitoringHubService } from '../../core/services/monitoring/monitoring-hub.service'
import { ServiceOrderService } from '../../core/services/service/service-order/service-order.service'
import { SnackbarCustomService } from '../../shared/components/snackbar/snackbar-custom.service'
import { DialogConfirmComponent } from '../../shared/components/dialog-confirm/dialog-confirm.component'
import {
  LinhaMonitoramentoVm,
  MonitoramentoSearchResponseVm,
  ResumoMonitoramentoVm,
  StatusServico,
} from '../../core/models/monitoring-os.model'

import { MonitoringOsSearchComponent } from './monitoring-os-search/monitoring-os-search.component'
import { TableListMonitoringOsComponent, ConfirmarFaseEvent } from '../../shared/components/table-list-monitoring-os/table-list-monitoring-os.component'
import { ModalPassageirosOsComponent } from '../../features/monitoring/modal-passageiros-os/modal-passageiros-os.component'
import { ModalProgressoOsComponent } from '../../features/monitoring-os/modal-progresso-os/modal-progresso-os.component'
import { ModalConfirmarFaseComponent } from '../../features/monitoring-os/modal-confirmar-fase/modal-confirmar-fase.component'
import { ModalMarcarNoShowComponent } from '../../features/monitoring-os/modal-marcar-no-show/modal-marcar-no-show.component'
import { ModalMensagemMotoristaComponent } from '../../features/monitoring-os/modal-mensagem-motorista/modal-mensagem-motorista.component'
import { ModalAlterarMotoristaComponent } from '../../features/monitoring-os/modal-alterar-motorista/modal-alterar-motorista.component'
import { ModalAlterarVeiculoComponent } from '../../features/monitoring-os/modal-alterar-veiculo/modal-alterar-veiculo.component'
import { ModalRecusarAtribuicaoComponent } from '../../features/monitoring-os/modal-recusar-atribuicao/modal-recusar-atribuicao.component'
import { ModalHistoricoAlteracoesComponent } from '../../features/monitoring-os/modal-historico-alteracoes/modal-historico-alteracoes.component'
import { OccurrencesModalTableComponent } from '../../features/occurrence/occurrences-modal-table/occurrences-modal-table.component'
import { LogisticsContractItemEditComponent } from '../../features/logistics/logistics-contract-item-edit/logistics-contract-item-edit.component'

// Torre de Monitoramento (sprints 9/10, tela id 73) — POST Monitoramento/search-paginated.
// Substitui a antiga "Monitoramento OS" sobre AgendaFrota/search-paginated/monitoramento
// (a rota antiga continua no ar, mas nada mais aqui a chama). Distinta de
// "Acompanhamento de OS" (pages/monitoring), que é a fila do operador.
@Component({
  selector: 'monitoring-os-component',
  imports: [
    FormsModule,
    NgClass,
    MatButtonModule,
    MatProgressBar,
    MatPaginatorModule,
    MatTabsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    TableListMonitoringOsComponent,
  ],
  templateUrl: './monitoring-os.component.html',
  styleUrl: './monitoring-os.component.scss',
  providers: [AuthService],
  standalone: true,
})
export class MonitoringOsComponent implements OnInit, OnDestroy {
  constructor(
    private monitoringService: MonitoringService,
    private hub: MonitoringHubService,
    private serviceOrderService: ServiceOrderService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackbar: SnackbarCustomService,
  ) {}

  page = 1
  rows = 15
  numberRows = 0
  itemsList: LinhaMonitoramentoVm[] = []
  resumo: ResumoMonitoramentoVm | null = null
  loading = false

  aba: 'ativos' | 'finalizados' = 'ativos'
  busca = ''
  statusFarolAtivo: StatusServico | null = null
  // Toggle independente dos chips de farol — semAlocacao não é uma cor do farol
  // (ver doc sprint 9/10 seção 06), então não entra no mesmo grupo de seleção única.
  semAlocacaoAtivo = false
  private advancedFilters: any = {}
  // Estado bruto do form (não o model já traduzido pro back) — só serve pra
  // reabrir o modal de filtros avançados já preenchido com o que foi aplicado.
  private advancedFiltersRaw: any = null
  private sort: { ordenarPor: string; direcao: 'asc' | 'desc' } | null = null

  agora = new Date()
  private serverOffsetMs = 0
  private clockTimer: ReturnType<typeof setInterval> | null = null
  private reconcileTimer: ReturnType<typeof setInterval> | null = null
  private diaAssinado: string | null = null

  private banidosNoShow = new Set<string>()
  // Colapsa rajada de OsAtualizada (várias fases/linhas em sequência) numa
  // única refetch silenciosa — o evento só traz osId+motivo, não a linha
  // pronta (contrato real do hub, ver monitoring-hub.service.ts).
  private osAtualizada$ = new Subject<void>()
  private osAtualizadaSub: Subscription | null = null

  screen = 'monitoramento'

  ngOnInit(): void {
    this.buscar()
    // Repintura do farol é local (alertaEm/criticoEm) — este intervalo só troca o
    // relógio que a tabela usa pra calcular a cor, nunca dispara requisição.
    this.clockTimer = setInterval(() => {
      this.agora = new Date(Date.now() + this.serverOffsetMs)
    }, 10000)
    // Cobre o caso do socket cair sem o cliente perceber — silencioso, sem spinner.
    this.reconcileTimer = setInterval(() => this.buscar(true), 5 * 60 * 1000)
    this.osAtualizadaSub = this.osAtualizada$.pipe(debounceTime(800)).subscribe(() => this.buscar(true))
    this.hub.linhaAtualizada$.subscribe(() => this.osAtualizada$.next())
  }

  ngOnDestroy(): void {
    if (this.clockTimer) clearInterval(this.clockTimer)
    if (this.reconcileTimer) clearInterval(this.reconcileTimer)
    this.osAtualizadaSub?.unsubscribe()
    this.hub.stop()
  }

  checkPermission(permission: string): boolean {
    return this.authService.checkPermission(permission, this.screen)
  }

  get noShows(): LinhaMonitoramentoVm[] {
    return this.itemsList.filter((i) => i.noShow && !this.banidosNoShow.has(i.noShow!.id))
  }

  dispensarBanner(id: string): void {
    this.banidosNoShow.add(id)
  }

  onPageEvent(event: PageEvent): void {
    this.page = event.pageIndex + 1
    this.rows = event.pageSize
    this.buscar()
  }

  onTab(aba: 'ativos' | 'finalizados'): void {
    this.aba = aba
    this.page = 1
    this.buscar()
  }

  onBusca(): void {
    this.page = 1
    this.buscar()
  }

  onLimparBusca(): void {
    this.busca = ''
    this.page = 1
    this.buscar()
  }

  onChipStatus(status: StatusServico | null): void {
    this.statusFarolAtivo = status
    this.page = 1
    this.buscar()
  }

  onChipSemAlocacao(): void {
    this.semAlocacaoAtivo = !this.semAlocacaoAtivo
    this.page = 1
    this.buscar()
  }

  onFiltrosAvancados(event: any): void {
    this.page = 1
    if (event === 'reset') {
      this.advancedFilters = {}
      this.advancedFiltersRaw = null
    } else {
      this.advancedFilters = event.model
      this.advancedFiltersRaw = event.raw
    }
    this.buscar()
  }

  abrirFiltrosAvancados(): void {
    const dialogRef = this.dialog.open(MonitoringOsSearchComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '95vw',
      data: this.advancedFiltersRaw,
    })
    dialogRef.componentInstance.response.subscribe((event: any) => this.onFiltrosAvancados(event))
  }

  private buildModel(): any {
    return {
      aba: this.aba,
      busca: this.busca || undefined,
      statusFarol: this.statusFarolAtivo || undefined,
      semAlocacao: this.semAlocacaoAtivo || undefined,
      ordenarPor: this.sort?.ordenarPor,
      direcao: this.sort?.direcao,
      ...this.advancedFilters,
    }
  }

  onSort(event: { ordenarPor: string; direcao: 'asc' | 'desc' } | null): void {
    this.sort = event
    this.page = 1
    this.buscar()
  }

  // O painel carrega o período inteiro em memória pra calcular o farol — validar
  // o teto de 31 dias antes de disparar evita uma ida que o back vai recusar de
  // qualquer forma (400 com mensagem pronta).
  private excedePeriodoMaximo(): boolean {
    const { dataInicio, dataFinal } = this.advancedFilters
    if (!dataInicio || !dataFinal) return false
    const d1 = this.parseDataBr(dataInicio)
    const d2 = this.parseDataBr(dataFinal)
    if (!d1 || !d2) return false
    const dias = Math.abs(d2.getTime() - d1.getTime()) / 86400000
    return dias > 31
  }

  private parseDataBr(data: string): Date | null {
    const partes = data.split('/').map(Number)
    if (partes.length !== 3) return null
    const [d, m, y] = partes
    return new Date(y, m - 1, d)
  }

  buscar(silencioso = false): void {
    if (this.excedePeriodoMaximo()) {
      this.snackbar.warning('Período máximo de 31 dias entre a data início e a data final.')
      return
    }
    if (!silencioso) this.loading = true
    this.monitoringService.searchPaginated(this.page, this.rows, this.buildModel()).subscribe({
      next: (res: any) => { this.treatResponse(res) },
      error: (err: any) => {
        if (!silencioso) this.loading = false
        this.snackbar.errorRequest(err)
      },
      complete: () => { if (!silencioso) this.loading = false },
    })
  }

  private treatResponse(response: any): void {
    const data: MonitoramentoSearchResponseVm | undefined = response.data
    this.itemsList = data?.itens ?? []
    this.resumo = data?.resumo ?? null
    this.numberRows = response.totalItens ?? 0

    // Desvio do relógio calculado uma vez por carga, nunca por tick — um cliente
    // adiantado acenderia vermelho antes da hora e ninguém desconfiaria.
    if (data?.servidorAgora) {
      this.serverOffsetMs = new Date(data.servidorAgora).getTime() - Date.now()
      this.agora = new Date(Date.now() + this.serverOffsetMs)
    }

    this.assinarDiaAtual()
  }

  private assinarDiaAtual(): void {
    const dataFiltro = this.advancedFilters.dataInicio || new Date().toLocaleDateString('pt-BR')
    const dia = this.paraIso(dataFiltro)
    if (dia === this.diaAssinado) return
    this.diaAssinado = dia
    this.hub.assinar(dia)
  }

  private paraIso(dataBr: string): string {
    const [d, m, y] = dataBr.split('/').map(Number)
    return `${y}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`
  }

  // --- Ações da linha ---

  // Mesmo modal de edição da aba "Itens Logística" (Logística → item de contrato).
  // Ele só aceita o Guid do ContratoItem, não o codigoOs.
  editarOs(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(LogisticsContractItemEditComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '100vw',
      width: '900px',
      maxHeight: '100vh',
      height: '100vh',
      panelClass: 'dialog-fullscreen',
      data: { id: item.contratoItemId, execucao: item },
    })
    dialogRef.componentInstance.response.subscribe((changed: boolean) => {
      if (changed) this.buscar(true)
    })
  }

  viewPassengers(item: LinhaMonitoramentoVm): void {
    this.dialog.open(ModalPassageirosOsComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '900px',
      maxWidth: '95vw',
      data: { osId: item.codigoOs },
    })
  }

  // Progresso do trajeto (status_ordem_servico_andamento) é conceito distinto do
  // farol de 4 fases da Torre — ver core/models/monitoring-os.model.ts.
  viewProgress(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(ModalProgressoOsComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '700px',
      maxWidth: '95vw',
      data: {
        item: {
          codigoOs: item.codigoOs,
          codigoTg: item.codigoTg,
          statusOsAndamentoId: item.statusOsAndamentoId,
          nome: item.motorista?.nome,
          placa: item.veiculo?.placa,
        },
      },
    })
    dialogRef.componentInstance.response.subscribe((changed: boolean) => {
      if (changed) this.buscar(true)
    })
  }

  viewMensagemMotorista(item: LinhaMonitoramentoVm): void {
    this.dialog.open(ModalMensagemMotoristaComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '500px',
      maxWidth: '95vw',
      data: { osId: item.codigoOs },
    })
  }

  alterarMotorista(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(ModalAlterarMotoristaComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '95vw',
      data: { item },
    })
    dialogRef.componentInstance.response.subscribe((changed: boolean) => {
      if (changed) this.buscar(true)
    })
  }

  alterarVeiculo(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(ModalAlterarVeiculoComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '95vw',
      data: { item },
    })
    dialogRef.componentInstance.response.subscribe((changed: boolean) => {
      if (changed) this.buscar(true)
    })
  }

  viewOccurrences(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(OccurrencesModalTableComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '900px',
      maxWidth: '95vw',
      data: { codigoOs: item.codigoOs, codigoTg: item.codigoTg },
    })
    dialogRef.afterClosed().subscribe(() => {
      // temOcorrencia da linha pode ter mudado.
      this.buscar(true)
    })
  }

  onConfirmarFase(event: ConfirmarFaseEvent): void {
    const dialogRef = this.dialog.open(ModalConfirmarFaseComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '480px',
      maxWidth: '95vw',
      data: { item: event.item, fase: event.fase },
    })
    dialogRef.componentInstance.response.subscribe((changed: boolean) => {
      if (changed) this.buscar(true)
    })
  }

  onMarcarNoShow(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(ModalMarcarNoShowComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '480px',
      maxWidth: '95vw',
      data: { item },
    })
    dialogRef.componentInstance.response.subscribe((changed: boolean) => {
      if (changed) this.buscar(true)
    })
  }

  // Reversível (dá pra marcar de novo), por isso não pede confirmação extra aqui.
  onDesmarcarNoShow(item: LinhaMonitoramentoVm): void {
    this.monitoringService.desmarcarNoShow(item.codigoOs).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message ?? 'No-show desfeito.')
        this.buscar(true)
      },
      error: (err: any) => this.snackbar.errorRequest(err),
    })
  }

  // Sem corpo e sem modal — o operador já mandou a OS pelo link do
  // mensagem-motorista, isto só registra. Clicar de novo (reenvio) é permitido e
  // só atualiza o carimbo.
  onConfirmarEnvio(item: LinhaMonitoramentoVm): void {
    this.monitoringService.confirmarEnvio(item.codigoOs).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message ?? 'Envio confirmado.')
        this.buscar(true)
      },
      error: (err: any) => this.snackbar.errorRequest(err),
    })
  }

  onDesfazerEnvio(item: LinhaMonitoramentoVm): void {
    this.monitoringService.desfazerEnvio(item.codigoOs).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message ?? 'Confirmação de envio desfeita.')
        this.buscar(true)
      },
      error: (err: any) => this.snackbar.errorRequest(err),
    })
  }

  // Sem corpo — só vale pra OS em 12 (Aguardando Confirmação Operador), o back
  // valida e recusa (400) fora disso. Fecha a compra e vai pra 5 Agendado.
  onConfirmarAtribuicao(item: LinhaMonitoramentoVm): void {
    this.monitoringService.confirmarAtribuicao(item.codigoOs).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message ?? 'Atribuição confirmada.')
        this.buscar(true)
      },
      error: (err: any) => this.snackbar.errorRequest(err),
    })
  }

  onRecusarAtribuicao(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(ModalRecusarAtribuicaoComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '480px',
      maxWidth: '95vw',
      data: { item },
    })
    dialogRef.componentInstance.response.subscribe((changed: boolean) => {
      if (changed) this.buscar(true)
    })
  }

  // POST OrdemServico/cancelar/{codigoOs} — mesmo endpoint usado em
  // modal-approve-contract-item (Logística). Destrutivo e sem volta, por isso passa
  // pelo DialogConfirmComponent antes de disparar.
  onCancelarOs(item: LinhaMonitoramentoVm): void {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '400px',
      maxWidth: '95vw',
      data: {
        title: 'Cancelar OS',
        message: `Deseja realmente cancelar a OS ${item.codigoOs}? Essa ação não pode ser desfeita.`,
        confirmLabel: 'Cancelar OS',
      },
    })
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return
      this.serviceOrderService.cancelServiceOrder(item.codigoOs).subscribe({
        next: (res: any) => {
          this.snackbar.success(res.message ?? 'OS cancelada com sucesso!')
          this.buscar(true)
        },
        error: (err: any) => this.snackbar.errorRequest(err),
      })
    })
  }

  // Puramente leitura — alteracoes já vem embutido na linha, sem chamada nova.
  onHistorico(item: LinhaMonitoramentoVm): void {
    this.dialog.open(ModalHistoricoAlteracoesComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '520px',
      maxWidth: '95vw',
      data: { item },
    })
  }
}
